import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { OperatorFunction, pipe } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import { ObservationEntity } from '../../coaching-base/config';
import {
    NewObservationParamsPostDto,
    ObservationDto,
    ObservationSummaryDto,
} from '../../coaching-base/general';
import {
    catchErrorLogRet,
    mapList,
    mapRetrieve,
    mapUpdate,
} from '../../common/rest.operators';

import { ObservationDtoTransformer } from './observation-dto.transformer';
import { ObservationSummaryDtoSetTransformer } from './observation-summary-dto-set.transformer';
import { ObservationSummaryDtoTransformer } from './observation-summary-dto.transformer';
import { ObservationEvent } from './observation.model';

@Injectable()
export class ObservationService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    // calling it a draft for the moment
    newDraft<T>(
        project: () => NewObservationParamsPostDto,
    ): OperatorFunction<T, ObservationDto> {
        return pipe(
            switchMap(() => {
                const body = project();
                return this._http.post(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/observations/new`,
                    body,
                );
            }),
            mapRetrieve(new ObservationDtoTransformer()),
            catchErrorLogRet(null),
        );
    }

    // TODO post-refactor. from observation.service>createObservation
    create<T>(
        project: (value: T, index: number) => ObservationEvent,
    ): OperatorFunction<T, ObservationDto> {
        return pipe(
            switchMap((value, index) => {
                const body = project(value, index);
                return this._http.post(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/observations`,
                    body,
                );
            }),
            // mapUpdate() // to be really restful 201 + location header
            mapRetrieve(new ObservationDtoTransformer()),
            // TODO catchError later
        );
    }

    // TODO post-refactor. from observation.service>updateObservation
    update<T>(
        project: (value: T, index: number) => ObservationEvent,
    ): OperatorFunction<T, ObservationDto> {
        return pipe(
            switchMap((value, index) => {
                const body = project(value, index);
                return this._http.put(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/observations/${body.eventId}`,
                    body,
                );
            }),
            // mapUpdate() // to be really restful 201 + location header
            mapRetrieve(new ObservationDtoTransformer()),
            // TODO catchError later
        );
    }

    // TODO post-refactor. from observation.service>getObservation
    retrieve<
        T,
        O extends {
            observationId: number;
            focusIds?: number[];
            focusStrs?: string[];
        },
    >(
        project: (value: T, index: number) => O,
    ): OperatorFunction<T, ObservationDto> {
        return pipe(
            switchMap((value, index) => {
                const args = project(value, index);
                let params = new HttpParams();
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-return-assign
                args.focusIds &&
                    args.focusIds.forEach(
                        (e) =>
                            (params = params.append('focusIds', e.toString())),
                    );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-return-assign
                args.focusStrs &&
                    args.focusStrs.forEach(
                        (e) =>
                            (params = params.append('focusStrs', e.toString())),
                    );
                return this._http.get(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/observations/${args.observationId}`,
                );
            }),
            mapRetrieve(new ObservationDtoTransformer()),
            catchErrorLogRet(null),
        );
    }

    // TODO post-refactor. from observation.service>getObservation
    retrieveSummary<
        T,
        O extends {
            observationId: number;
            focusIds?: number[];
            focusStrs?: string[];
        },
    >(
        project: (value: T, index: number) => O,
    ): OperatorFunction<T, ObservationSummaryDto> {
        return pipe(
            switchMap((value, index) => {
                const args = project(value, index);
                let params = new HttpParams();
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-return-assign
                args.focusIds &&
                    args.focusIds.forEach(
                        (e) =>
                            (params = params.append('focusIds', e.toString())),
                    );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-return-assign
                args.focusStrs &&
                    args.focusStrs.forEach(
                        (e) =>
                            (params = params.append('focusStrs', e.toString())),
                    );
                return this._http.get(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/observations/${args.observationId}/summary`,
                    { params },
                );
            }),
            mapRetrieve(new ObservationSummaryDtoTransformer()),
            catchErrorLogRet(null),
        );
    }

    delete<T>(
        project: (value: T, index: number) => number,
    ): OperatorFunction<T, ObservationEvent> {
        return pipe(
            switchMap((value, index) => {
                const eventId = project(value, index);
                return this._http.delete(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/observations/${eventId}`,
                );
            }),
            mapUpdate(),
        );
    }

    listSummary<T>(
        project: (value: T, index: number) => number,
    ): OperatorFunction<T, ObservationSummaryDto[]> {
        return pipe(
            switchMap((value, index) => {
                const userId = project(value, index);
                const params = new HttpParams().set(
                    'userId',
                    userId.toString(),
                );
                return this._http.get(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/observations/summary`,
                    { params },
                );
            }),
            mapList(new ObservationSummaryDtoSetTransformer()),
            catchErrorLogRet([]),
        );
    }

    /// /////////////////////////////////////////////////

    // TODO post-refactor. from coaching.config.service>retrieveCoachingConfigJSON
    retrieveObservationConfig<T>(): OperatorFunction<T, ObservationEntity> {
        return pipe(
            switchMap(() =>
                this._http.get(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/observations/config`,
                ),
            ),
            mapRetrieve(),
            map((ret) => JSON.parse(ret)),
            catchErrorLogRet({}),
        );
    }
}
